import { faceAt, overlayFadeMs, overlayOpacity, timelineToSource, type Clip, type Overlay } from "@hudhud/core";
import { HEIGHT, WIDTH, frameToMs } from "../types.js";
import { cropAt, cropBox } from "./video.js";

/** The entrance as numbers: how far in it is (0..1 eased) decides the offset or scale that carries it there. */
export function enterMotion(enter: Overlay["enter"], t: number): { tx?: number; ty?: number; scale?: number } {
  const e = 1 - Math.pow(1 - t, 3);
  if (enter === "slide") return { tx: (1 - e) * -140 };
  if (enter === "rise") return { ty: (1 - e) * 60 };
  if (enter === "pop") return { scale: 0.82 + 0.18 * e + (t < 1 ? Math.sin(t * Math.PI) * 0.05 : 0) };
  return {};
}
/** The entrance as CSS. */
export function enterTransform(enter: Overlay["enter"], t: number): string {
  const m = enterMotion(enter, t);
  if (m.tx !== undefined) return `translateX(${m.tx}px)`;
  if (m.ty !== undefined) return `translateY(${m.ty}px)`;
  if (m.scale !== undefined) return `scale(${m.scale})`;
  return "";
}
/** The motion while it shows, as numbers: a slow push in, a slow drift across (per cent of its own width), or nothing. `p` = 0..1 through the span. */
export function motionValues(motion: Overlay["motion"], p: number): { scale?: number; txPct?: number } {
  if (motion === "push") return { scale: 1 + 0.05 * p };
  if (motion === "drift") return { scale: 1.08, txPct: -3 + 6 * p };
  return {};
}
/** The motion as CSS. */
export function motionTransform(motion: Overlay["motion"], p: number): string {
  const m = motionValues(motion, p);
  if (motion === "push" && m.scale !== undefined) return `scale(${m.scale})`;
  if (motion === "drift" && m.scale !== undefined && m.txPct !== undefined) return `scale(${m.scale}) translateX(${m.txPct.toFixed(2)}%)`;
  return "";
}

/** A card beside the face: on whichever side has more room, level with the eyes, kept inside the frame. */
export function followPlacement(face: { x: number; y: number }, w: number, h: number): { left: number; top: number } {
  const gap = 36;
  const right = face.x < WIDTH / 2;
  const left = right ? Math.min(WIDTH - w - 24, face.x + WIDTH * 0.16 + gap) : Math.max(24, face.x - WIDTH * 0.16 - gap - w);
  const top = Math.max(120, Math.min(HEIGHT - h - 260, face.y - h / 2));
  return { left, top };
}

/** Where the face sits on the canvas at a timeline moment (from the tracked points through the crop), or null without tracking. */
export function faceOnCanvas(clip: Pick<Clip, "facePoints" | "layout" | "segments" | "crop">, timelineMs: number): { x: number; y: number } | null {
  if (!clip.facePoints.length || clip.layout !== "crop") return null;
  const srcMs = timelineToSource(clip.segments, timelineMs);
  const f = faceAt(clip.facePoints, srcMs);
  const cs = cropBox(cropAt(clip.crop, srcMs));
  return { x: cs.left + f.cx * cs.width, y: cs.top + f.cy * cs.height };
}

/** The face's height on the canvas (0..1) while a picture shows, for placing a card clear of it; null when unknown. */
export function overlayFaceY(clip: Pick<Clip, "layout" | "crop">, overlay: Pick<Overlay, "startMs">): number | null {
  if (clip.layout === "crop" && clip.crop.length) { const k = cropAt(clip.crop, overlay.startMs); const cs = cropBox(k); return (cs.top + k.cy * cs.height) / HEIGHT; }
  if (clip.layout === "split") return 0.4;
  return null;
}

/** One picture's state `frame` frames into its `frames`-long span: the fade, the entrance and the slow motion, all 0..1. */
export function overlayFrame(overlay: Pick<Overlay, "motion">, frame: number, frames: number): { enterT: number; inOut: number; drift: number; moveP: number } {
  const spanMs = frameToMs(frames);
  const enterT = Math.min(1, frameToMs(frame) / overlayFadeMs(spanMs));
  const inOut = overlayOpacity(frameToMs(frame), spanMs);
  const p = Math.min(1, frame / Math.max(1, frames));
  const drift = 1 + 0.05 * p * (overlay.motion === "push" ? 1 : 0);
  return { enterT, inOut, drift, moveP: p };
}

/** Size of the corner polaroid and of a following card, from the style's own rules. */
export const CORNER_WIDTH = (scale: number) => WIDTH * 0.4 * scale;
export const FOLLOW_SHRINK = 0.62;
